import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { isProductEnabled } from "@/business/product-access";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { ProductMarketingPage } from "./product-marketing-page";
import { getMarketingProduct } from "./routes";

export const ProductMarketingBoundary = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  const { businessId } = useBusiness();
  const { settings, isLoading, hasValidSettings } = useBusinessProducts();
  const { data: user } = useGetMyUser();
  const product = getMarketingProduct(pathname);
  if (!product || !isAdminOrOwner(user?.role)) return children;
  if (isLoading) return <LoadingSpinner />;
  if (!hasValidSettings)
    return (
      <Stack className="p-8">
        <Typography role="alert" textColor="negative">
          Could not load product settings. Refresh the page and try again.
        </Typography>
      </Stack>
    );
  if (!isProductEnabled(settings, product))
    return (
      <ProductMarketingPage
        key={`${businessId}-${product}`}
        product={product}
      />
    );
  return children;
};
